if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      this.onVariantChangeHandler = this.onVariantChange.bind(this);
      this.onVariantChangedHandler = this.onVariantChanged.bind(this);
      document.addEventListener('change', this.onVariantChangeHandler, true);
      document.addEventListener('input', this.onVariantChangeHandler, true);
      document.addEventListener('click', this.onVariantChangeHandler, true);
      document.addEventListener('variant:change', this.onVariantChangedHandler, true);
      document.addEventListener('variant:changed', this.onVariantChangedHandler, true);
      if (!this.elements.thumbnails) return;

      const debounceFunction = typeof window.debounce === 'function' ? window.debounce : this.debounce;
      this.onSlideChangedHandler = debounceFunction(this.onSlideChanged.bind(this), 500);
      this.elements.viewer.addEventListener('slideChanged', this.onSlideChangedHandler);
      this.onThumbnailClickHandler = this.onThumbnailClick.bind(this);
      this.thumbnailButtons = Array.from(this.elements.thumbnails.querySelectorAll('[data-target] button'));
      this.thumbnailButtons.forEach((button) => button.addEventListener('click', this.onThumbnailClickHandler));
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onVariantChangeHandler, true);
      document.removeEventListener('input', this.onVariantChangeHandler, true);
      document.removeEventListener('click', this.onVariantChangeHandler, true);
      document.removeEventListener('variant:change', this.onVariantChangedHandler, true);
      document.removeEventListener('variant:changed', this.onVariantChangedHandler, true);
      if (this.elements.viewer && this.onSlideChangedHandler) {
        this.elements.viewer.removeEventListener('slideChanged', this.onSlideChangedHandler);
      }
      if (this.thumbnailButtons && this.onThumbnailClickHandler) {
        this.thumbnailButtons.forEach((button) => button.removeEventListener('click', this.onThumbnailClickHandler));
      }
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    onThumbnailClick(event) {
      const mediaToSwitch = event.currentTarget.closest('[data-target]');
      if (!mediaToSwitch) return;

      event.preventDefault();
      this.setActiveMedia(mediaToSwitch.dataset.target, false);
    }

    onVariantChange(event) {
      if (!event.target.closest || !event.target.closest(`variant-selects[data-section="${this.dataset.section}"]`)) return;

      window.setTimeout(() => this.updateActiveMediaFromVariant(), 0);
      window.setTimeout(() => this.updateActiveMediaFromVariant(), 150);
    }

    onVariantChanged(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant || !this.isVariantEventForThisGallery(event)) return;

      this.updateActiveMediaFromVariant(variant);
    }

    updateActiveMediaFromVariant(variant = this.getSelectedVariant()) {
      const mediaId = this.getSelectedVariantMediaId(variant);
      if (!mediaId) return;

      this.setActiveMedia(mediaId, true);
    }

    getSelectedVariantMediaId(variant) {
      if (!variant) return null;

      return this.getVariantMediaMap()[variant.id] || this.getMediaIdFromVariant(variant);
    }

    getMediaIdFromVariant(variant) {
      const mediaId = variant.featured_media && variant.featured_media.id;
      if (mediaId) return `${this.dataset.section}-${mediaId}`;

      return null;
    }

    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      if (!selectedOptions.length) return null;

      return this.getVariantData().find((variant) => {
        return variant.options.every((option, index) => selectedOptions[index] === option);
      });
    }

    getSelectedOptions() {
      const variantPicker = this.getVariantPicker();
      if (!variantPicker) return [];

      return Array.from(variantPicker.querySelectorAll('.product-form__input')).map((optionGroup) => {
        const select = optionGroup.querySelector('select');
        if (select) return select.value;

        const checkedInput = optionGroup.querySelector('input[type="radio"]:checked');
        return checkedInput && checkedInput.value;
      }).filter(Boolean);
    }

    getVariantData() {
      const variantPicker = this.getVariantPicker();
      const variantData = variantPicker && variantPicker.querySelector('script[type="application/json"]:not([data-variant-media-map])');
      return variantData ? JSON.parse(variantData.textContent) : [];
    }

    getVariantMediaMap() {
      const variantPicker = this.getVariantPicker();
      const mediaMap = variantPicker && variantPicker.querySelector('[data-variant-media-map]');
      return mediaMap ? JSON.parse(mediaMap.textContent) : {};
    }

    getVariantPicker() {
      return document.querySelector(`variant-selects[data-section="${this.dataset.section}"]`);
    }

    isVariantEventForThisGallery(event) {
      const variantPicker = event.target.closest && event.target.closest('variant-selects, variant-radios');
      if (!variantPicker) return true;

      return variantPicker.dataset.section === this.dataset.section || variantPicker.dataset.originalSection === this.dataset.section;
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: prepend ? 0 : activeMedia.offsetLeft, behavior: 'smooth' });
          if (this.elements.viewer.update) {
            window.setTimeout(() => this.elements.viewer.update(), 250);
          }
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      if (!activeThumbnail) return;

      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    debounce(callback, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => callback(...args), wait);
      };
    }
  });
}
